import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Copy, ShieldCheck } from "lucide-react";

export default function AdminWorkspaces() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", owner_email: "" });
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/workspaces").then((r) => setList(r.data.workspaces)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/workspaces", form);
      setToken(data.owner_invite_token);
      setForm({ name: "", owner_email: "" });
      toast.success("Workspace created — deliver the token manually");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full bg-navy border border-white/15 text-white px-3.5 py-2.5 text-sm focus:border-safety outline-none";

  return (
    <div data-testid="admin-workspaces">
      <div className="bg-safety/10 border border-safety/30 p-4 mb-6 flex items-start gap-3">
        <ShieldCheck size={18} className="text-safety shrink-0 mt-0.5" />
        <p className="text-sm text-slate-300 leading-relaxed">
          Private tenant workspaces (FamilyBase pilot). Creating a workspace returns a single-use
          owner claim token — no email is sent. Deliver it to the owner yourself.
        </p>
      </div>

      <form onSubmit={create} className="bg-charcoal border border-white/10 p-6 space-y-4 max-w-xl">
        <p className="text-slate-400 text-xs font-mono uppercase tracking-wider">New workspace</p>
        <input data-testid="ws-create-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Workspace name (e.g. FamilyBase — Cousin)" className={inputCls} required minLength={2} />
        <input data-testid="ws-create-owner" type="email" value={form.owner_email}
          onChange={(e) => setForm({ ...form, owner_email: e.target.value })} placeholder="Owner email" className={inputCls} required />
        <button data-testid="ws-create-submit" disabled={busy}
          className="bg-safety hover:bg-safety-hover disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          {busy ? "Creating…" : "Create workspace"}
        </button>
        {token && (
          <div className="bg-safety/10 border border-safety/30 p-4" data-testid="ws-token-box">
            <p className="text-xs text-slate-300">Owner claim token (single-use, 72 hours, bound to that email):</p>
            <p className="font-mono text-xs text-safety break-all mt-2" data-testid="ws-owner-token">{token}</p>
            <button type="button" data-testid="ws-token-copy"
              onClick={() => { navigator.clipboard.writeText(token); toast.success("Copied"); }}
              className="mt-2 text-xs text-white flex items-center gap-1.5 hover:text-safety"><Copy size={12} /> Copy token</button>
          </div>
        )}
      </form>

      <div className="mt-8 bg-charcoal border border-white/10">
        <p className="text-slate-400 text-xs font-mono uppercase tracking-wider p-4 border-b border-white/10">Existing workspaces</p>
        {list.map((w) => (
          <div key={w.workspace_id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 text-sm"
            data-testid={`ws-row-${w.workspace_id.slice(0, 8)}`}>
            <span className="text-white">{w.name}</span>
            <span className="text-slate-500 font-mono text-xs">{w.owner_email} · {String(w.created_at).slice(0, 10)}</span>
          </div>
        ))}
        {!list.length && <p className="text-slate-500 text-sm p-4">No workspaces yet.</p>}
      </div>
    </div>
  );
}
